import React, { useState } from "react";
import Navbar from "@/components/storefront/Navbar";
import Footer from "@/components/storefront/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, Phone, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: { en: "Pending", ar: "قيد الانتظار", cls: "border-warning/30 text-warning bg-warning/10" },
  processing: { en: "Processing", ar: "قيد التنفيذ", cls: "border-primary/30 text-primary bg-accent" },
  done: { en: "Done", ar: "مكتمل", cls: "border-success/30 text-success bg-success/10" },
  cancelled: { en: "Cancelled", ar: "ملغى", cls: "border-destructive/30 text-destructive bg-destructive/10" },
  paid: { en: "Paid", ar: "مدفوع", cls: "border-success/30 text-success bg-success/5" },
  shipped: { en: "Shipped", ar: "تم الشحن", cls: "border-primary/30 text-primary bg-accent" },
  delivered: { en: "Delivered", ar: "تم التسليم", cls: "border-success/30 text-success bg-success/10" },
};

export default function MyOrders() {
  const { t, lang } = useApp();
  const [phone, setPhone] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSearch = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/my/orders`, { params: { mobile: phone.trim() } });
      setOrders(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };

  const download = async (orderId) => {
    try {
      const res = await fetch(`${API}/orders/${orderId}/download`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || (lang === "ar" ? "الملف غير جاهز" : "File not ready"));
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : "download";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("[download] failed", e);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <h1 className="text-3xl font-bold mb-2">{lang === "ar" ? "تتبع طلباتي" : "Track my orders"}</h1>
        <p className="text-muted-foreground mb-6 text-sm">
          {lang === "ar" ? "أدخل رقم جوالك لعرض حالة طلباتك." : "Enter your phone to see your order status."}
        </p>

        <form onSubmit={onSearch} className="flex gap-2 mb-8" data-testid="my-orders-form">
          <div className="relative flex-1">
            <Phone className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="my-orders-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+9665..."
              className="ps-9"
            />
          </div>
          <Button type="submit" disabled={loading} className="gap-2" data-testid="my-orders-search">
            <Search className="h-4 w-4" />
            {lang === "ar" ? "بحث" : "Search"}
          </Button>
        </form>

        {orders !== null && (
          <div className="space-y-3" data-testid="my-orders-list">
            {orders.length === 0 && (
              <p className="text-center py-12 text-muted-foreground" data-testid="no-orders-found">
                {lang === "ar" ? "لا يوجد طلبات لهذا الرقم" : "No orders found for this number"}
              </p>
            )}
            {orders.map((o) => {
              const st = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
              return (
                <div key={o.id} data-testid={`my-order-${o.id}`} className="bg-surface border border-border rounded-xl p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-semibold">{o.order_number}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {new Date(o.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
                    </div>
                  </div>
                  <div className="font-bold">{formatSAR(o.total, lang)}</div>
                  <Badge variant="outline" className={st.cls}>
                    {lang === "ar" ? st.ar : st.en}
                  </Badge>
                  {o.payment_status === "paid" && o.status !== "cancelled" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => download(o.id)}
                      data-testid={`my-order-download-${o.id}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {lang === "ar" ? "تحميل" : "Download"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
